"use client";

import React, { useEffect, useMemo, useState } from "react";
import { WorkOrder, Chemical, ServiceLocation, AppSettings } from "@/lib/types";
import { useOperations, useUnitOfWork } from "@/lib/data/provider";
import { checkAnnualActiveIngredientCaps } from "@/lib/rotation-policy";
import { completeWorkOrderLocal } from "@/lib/transactions/work-order";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckSquare, ClipboardList, Clock, CloudSun, Filter, ListTodo, MessageSquare, Plus, Route, Thermometer, Trash2, Truck, Wind } from "lucide-react";
import { LogisticsPortal } from "@/components/equipment/LogisticsPortal";
import type { Vehicle } from "@/lib/types";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface FieldWorkOrderPadProps {
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  activeVehicleId?: string;
  activeWorkOrder?: WorkOrder | null;
  workOrders?: WorkOrder[];
  onSelectWorkOrder?: (workOrderId: string) => void;
  onSaveWorkOrder?: (workOrder: WorkOrder) => Promise<void>;
  vehicleLabel?: string;
  currentTimeLabel?: string;
  elapsedLabel?: string;
  activeMechanic?: string;
  onClockIn?: () => void;
  onClockOut?: () => void;
  isClockedIn?: boolean;
}

interface WorkOrderPadDraft {
  roads: Array<{ roadName: string; acres: string; applicationMethod: string }>;
  manualAreas: Array<{ name: string; acres: string; applicationMethod: string }>;
  chemicals: Array<{ name: string; rate: string; unit: string; lotNumber: string; epaRegistrationNumber?: string }>;
  applicatorsCsv: string;
  applicatorLicense: string;
  startTemp: string;
  startWindSpeed: string;
  startWindDir: string;
  endTemp: string;
  endWindSpeed: string;
  endWindDir: string;
  distanceToWater: string;
  applicationSite: string;
  totalMixVolume: string;
  mixRate: string;
  totalAcres: string;
  otherEquipment: string;
  projectName: string;
  customerName: string;
  customerContact: string;
  customerPhone: string;
  customerEmail: string;
  customerAddress: string;
  date: string;
  appDate: string;
  startTime: string;
  endTime: string;
  county: string;
  status: "Ready" | "In Progress" | "Completed" | "On Hold";
  gpa: string;
  assignedToCsv: string;
  equipmentCsv: string;
  targetWeedsCsv: string;
  workRequested: string;
  sectionBreakdown: string;
  privateNotes: string;
  presetType: "maintenance" | "standard-spray" | "custom";
  completionFields: string;
  roadOrAreaLink: string;
  whatWentWell: string;
  needsAttention: string;
  customerFacingNotes: string;
  followUpDate: string;
}

const EMPTY_DRAFT: WorkOrderPadDraft = {
  roads: [],
  manualAreas: [],
  chemicals: [],
  applicatorsCsv: "",
  applicatorLicense: "",
  startTemp: "",
  startWindSpeed: "",
  startWindDir: "",
  endTemp: "",
  endWindSpeed: "",
  endWindDir: "",
  distanceToWater: "",
  applicationSite: "",
  totalMixVolume: "",
  mixRate: "",
  totalAcres: "",
  otherEquipment: "",
  projectName: "",
  customerName: "",
  customerContact: "",
  customerPhone: "",
  customerEmail: "",
  customerAddress: "",
  date: "",
  appDate: "",
  startTime: "",
  endTime: "",
  county: "",
  status: "Ready",
  gpa: "",
  assignedToCsv: "",
  equipmentCsv: "",
  targetWeedsCsv: "",
  workRequested: "",
  sectionBreakdown: "",
  privateNotes: "",
  presetType: "maintenance",
  completionFields: "",
  roadOrAreaLink: "",
  whatWentWell: "",
  needsAttention: "",
  customerFacingNotes: "",
  followUpDate: "",
};

export function FieldWorkOrderPad({
  open: propOpen,
  isOpen,
  onOpenChange,
  onClose,
  activeVehicleId,
  activeWorkOrder,
  workOrders = [],
  onSelectWorkOrder,
  onSaveWorkOrder,
  vehicleLabel,
  currentTimeLabel = '',
  elapsedLabel = '',
  activeMechanic = 'Lead Applicator',
  onClockIn,
  onClockOut,
  isClockedIn = false,
}: FieldWorkOrderPadProps) {
  const open = propOpen ?? isOpen ?? false;
  const handleOpenChange = (val: boolean) => {
    onOpenChange?.(val);
    if (!val) onClose?.();
  };
  const uow = useUnitOfWork();
  const { dailyOpLogs, vehicles: vehiclesRepo } = uow;
  const operations = useOperations();

  const [internalWorkOrders, setInternalWorkOrders] = useState<WorkOrder[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (workOrders && workOrders.length > 0) {
        setInternalWorkOrders(workOrders);
      } else {
        try {
          const list = await uow.workOrders.list();
          if (active) setInternalWorkOrders(list as unknown as WorkOrder[]);
        } catch (e) {
          console.error("Failed to load work orders in FieldWorkOrderPad", e);
        }
      }
    })();
    return () => { active = false; };
  }, [uow, workOrders]);

  const [page, setPage] = useState<"page1" | "page2" | "page3">("page1");
  const [draft, setDraft] = useState<WorkOrderPadDraft>(EMPTY_DRAFT);
  const [workOrderSearch, setWorkOrderSearch] = useState("");
  const [workOrderFilter, setWorkOrderFilter] = useState<"active" | "all" | "completed">("active");
  const [isSelectingWorkOrder, setIsSelectingWorkOrder] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [opsMode, setOpsMode] = useState<"mix" | "travel" | "fuel-up">("mix");
  const [activeOpsLogId, setActiveOpsLogId] = useState<string | null>(null);
  const [isModeSwitching, setIsModeSwitching] = useState(false);
  const [activeVehicle, setActiveVehicle] = useState<Vehicle | null>(null);
  const [logisticsOpen, setLogisticsOpen] = useState(false);
  const [logisticsTab, setLogisticsTab] = useState<string>("station");

  const assignedTruckLabel = useMemo(() => {
    const equipmentList = activeWorkOrder?.equipment || [];
    const explicitTruck = equipmentList.find((item) => /truck|trk/i.test(item || ''));
    return explicitTruck || equipmentList[0] || vehicleLabel || "";
  }, [activeWorkOrder?.equipment, vehicleLabel]);

  const storageKey = useMemo(() => {
    const woId = activeWorkOrder?.id || "no-work-order";
    return `ram.field.workorder.pad.${woId}`;
  }, [activeWorkOrder?.id]);

  useEffect(() => {
    if (!open) return;
    setIsSelectingWorkOrder(!activeWorkOrder?.id);
  }, [open, activeWorkOrder?.id]);

  useEffect(() => {
    if (!open) return;

    let isCancelled = false;

    const hydrateOpsModeContext = async () => {
      try {
        const logs = await dailyOpLogs.list();
        const today = new Date().toISOString().split("T")[0];
        const activeToday = logs.filter((log) => log.date === today && log.status === "active");

        const vehicleHint = String(vehicleLabel || assignedTruckLabel || "").toLowerCase();
        const matchedLog = activeToday.find((log) => {
          if (!vehicleHint) return false;
          const vehicleName = String((log as unknown as { vehicleName?: string }).vehicleName || "").toLowerCase();
          const vehicleId = String(log.vehicleId || "").toLowerCase();
          return vehicleName.includes(vehicleHint) || vehicleId.includes(vehicleHint.replace(/\s+/g, ""));
        }) || activeToday[0];

        if (isCancelled) return;

        setActiveOpsLogId(matchedLog?.id || null);

        const timeline = matchedLog?.timeline || [];
        const lastType = timeline.length ? timeline[timeline.length - 1]?.type : undefined;
        if (lastType === "fueling") {
          setOpsMode("fuel-up");
        } else if (lastType === "mix") {
          setOpsMode("mix");
        } else {
          setOpsMode("travel");
        }

        if (matchedLog?.vehicleId) {
          try {
            const v = await uow.vehicles.get(matchedLog.vehicleId);
            if (!isCancelled && v) setActiveVehicle(v as unknown as Vehicle);
          } catch {
            // vehicle lookup failed — portal will remain disabled
          }
        }
      } catch {
        if (!isCancelled) {
          setActiveOpsLogId(null);
        }
      }
    };

    void hydrateOpsModeContext();

    return () => {
      isCancelled = true;
    };
  }, [open, dailyOpLogs, vehicleLabel, assignedTruckLabel, uow.vehicles]);

  useEffect(() => {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as WorkOrderPadDraft;
        setDraft({ ...EMPTY_DRAFT, ...parsed });
        return;
      } catch {
        // Fall through to defaults if parse fails.
      }
    }

    setDraft({
      ...EMPTY_DRAFT,
      roads: (activeWorkOrder?.roads || []).map((road) => ({
        roadName: road.roadName || road.name || "",
        acres: road.acres !== undefined && road.acres !== null ? String(road.acres) : "",
        applicationMethod: road.applicationMethod || "",
      })),
      manualAreas: (activeWorkOrder?.manualAreas || []).map((area) => ({
        name: area.name || "",
        acres: area.acres !== undefined && area.acres !== null ? String(area.acres) : "",
        applicationMethod: area.applicationMethod || "",
      })),
      chemicals: (activeWorkOrder?.chemicals || []).map((chem) => ({
        name: chem.name || "",
        rate: chem.rate !== undefined && chem.rate !== null ? String(chem.rate) : "",
        unit: chem.unit || "",
        lotNumber: chem.lotNumber || chem.inventoryLogId || "",
        epaRegistrationNumber: chem.epaRegistrationNumber || "",
      })),
      applicatorsCsv: (activeWorkOrder?.applicatorSelection || activeWorkOrder?.applicators?.map((a) => a.name) || []).join(", "),
      applicatorLicense: activeWorkOrder?.applicatorLicense || "",
      startTemp: activeWorkOrder?.startWeather?.temperature || "",
      startWindSpeed: activeWorkOrder?.startWeather?.windSpeed || "",
      startWindDir: activeWorkOrder?.startWeather?.windDirection || "",
      endTemp: activeWorkOrder?.endWeather?.temperature || "",
      endWindSpeed: activeWorkOrder?.endWeather?.windSpeed || "",
      endWindDir: activeWorkOrder?.endWeather?.windDirection || "",
      distanceToWater: activeWorkOrder?.distanceToWater || "",
      applicationSite: activeWorkOrder?.applicationSite || "",
      totalMixVolume: activeWorkOrder?.totalMixVolume !== undefined && activeWorkOrder?.totalMixVolume !== null ? String(activeWorkOrder.totalMixVolume) : "",
      mixRate: activeWorkOrder?.mixRate !== undefined && activeWorkOrder?.mixRate !== null ? String(activeWorkOrder.mixRate) : "",
      totalAcres: activeWorkOrder?.totalAcres !== undefined && activeWorkOrder?.totalAcres !== null ? String(activeWorkOrder.totalAcres) : "",
      otherEquipment: activeWorkOrder?.otherEquipment || "",
      projectName: activeWorkOrder?.projectName || "",
      customerName: activeWorkOrder?.customerName || "",
      customerContact: activeWorkOrder?.customerContact || "",
      customerPhone: activeWorkOrder?.customerPhone || "",
      customerEmail: activeWorkOrder?.customerEmail || "",
      customerAddress: activeWorkOrder?.customerAddress || "",
      date: activeWorkOrder?.date || "",
      appDate: activeWorkOrder?.appDate || "",
      startTime: activeWorkOrder?.startTime || "",
      endTime: activeWorkOrder?.endTime || "",
      county: activeWorkOrder?.county || "",
      status: (activeWorkOrder?.status === 'Archived' ? 'Ready' : (activeWorkOrder?.status || 'Ready')) as WorkOrderPadDraft['status'],
      gpa: activeWorkOrder?.gpa !== undefined && activeWorkOrder?.gpa !== null ? String(activeWorkOrder.gpa) : "",
      assignedToCsv: (activeWorkOrder?.assignedTo || []).join(", "),
      equipmentCsv: (activeWorkOrder?.equipment || []).join(", "),
      targetWeedsCsv: (activeWorkOrder?.targetWeeds || []).join(", "),
      workRequested: activeWorkOrder?.title || "",
      privateNotes: activeWorkOrder?.notes || "",
    });
  }, [storageKey, activeWorkOrder]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      window.localStorage.setItem(storageKey, JSON.stringify(draft));
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [storageKey, draft]);

  const setField = <K extends keyof WorkOrderPadDraft>(key: K, value: WorkOrderPadDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const parseCsv = (value: string) => value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  const parseNumber = (value: string) => {
    if (!value.trim()) return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  };

  const updateRoad = (index: number, key: "roadName" | "acres" | "applicationMethod", value: string) => {
    setDraft((prev) => ({
      ...prev,
      roads: prev.roads.map((road, i) => (i === index ? { ...road, [key]: value } : road)),
    }));
  };

  const updateManualArea = (index: number, key: "name" | "acres" | "applicationMethod", value: string) => {
    setDraft((prev) => ({
      ...prev,
      manualAreas: prev.manualAreas.map((area, i) => (i === index ? { ...area, [key]: value } : area)),
    }));
  };

  const updateChemical = (index: number, key: "name" | "rate" | "unit" | "lotNumber" | "epaRegistrationNumber", value: string) => {
    setDraft((prev) => ({
      ...prev,
      chemicals: prev.chemicals.map((chem, i) => (i === index ? { ...chem, [key]: value } : chem)),
    }));
  };

  const handleSaveToWorkOrder = async () => {
    if (!activeWorkOrder?.id) return;

    if (draft.status === 'Completed' && activeWorkOrder.status !== 'Invoiced' && activeWorkOrder.status !== 'Completed') {
      alert('Completed requires the work order to be Invoiced first (invoiced and paid).');
      return;
    }

    const next: WorkOrder = {
      ...activeWorkOrder,
      title: draft.workRequested,
      projectName: draft.projectName || null,
      customerName: draft.customerName || null,
      customerContact: draft.customerContact,
      customerPhone: draft.customerPhone,
      customerEmail: draft.customerEmail,
      customerAddress: draft.customerAddress,
      date: draft.date,
      appDate: draft.appDate,
      startTime: draft.startTime,
      endTime: draft.endTime,
      county: draft.county,
      status: draft.status as WorkOrder['status'],
      gpa: draft.gpa ? Number(draft.gpa) : activeWorkOrder.gpa,
      assignedTo: parseCsv(draft.assignedToCsv),
      equipment: parseCsv(draft.equipmentCsv),
      targetWeeds: parseCsv(draft.targetWeedsCsv),
      applicatorSelection: parseCsv(draft.applicatorsCsv),
      applicatorLicense: draft.applicatorLicense,
      startWeather: {
        ...(activeWorkOrder.startWeather || {}),
        temperature: draft.startTemp,
        windSpeed: draft.startWindSpeed,
        windDirection: draft.startWindDir,
      },
      endWeather: {
        ...(activeWorkOrder.endWeather || {}),
        temperature: draft.endTemp,
        windSpeed: draft.endWindSpeed,
        windDirection: draft.endWindDir,
      },
      distanceToWater: draft.distanceToWater,
      applicationSite: draft.applicationSite,
      totalMixVolume: parseNumber(draft.totalMixVolume),
      mixRate: parseNumber(draft.mixRate),
      totalAcres: parseNumber(draft.totalAcres),
      otherEquipment: draft.otherEquipment,
      roads: draft.roads
        .filter((row) => row.roadName.trim())
        .map((row, index) => ({
          ...(activeWorkOrder.roads?.[index] || {}),
          roadName: row.roadName.trim(),
          name: row.roadName.trim(),
          acres: row.acres ? Number(row.acres) : undefined,
          applicationMethod: (row.applicationMethod as NonNullable<WorkOrder['roads']>[number]['applicationMethod']) || activeWorkOrder.roads?.[index]?.applicationMethod,
        })),
      manualAreas: draft.manualAreas
        .filter((row) => row.name.trim())
        .map((row, index) => ({
          ...(activeWorkOrder.manualAreas?.[index] || {}),
          name: row.name.trim(),
          acres: row.acres ? Number(row.acres) : undefined,
          applicationMethod: (row.applicationMethod as NonNullable<WorkOrder['manualAreas']>[number]['applicationMethod']) || activeWorkOrder.manualAreas?.[index]?.applicationMethod,
        })),
      chemicals: draft.chemicals
        .filter((row) => row.name.trim())
        .map((row, index) => ({
          ...(activeWorkOrder.chemicals?.[index] || {}),
          id: activeWorkOrder.chemicals?.[index]?.id || `${activeWorkOrder.id}-chem-${index}`,
          name: row.name.trim(),
          rate: row.rate ? Number(row.rate) : 0,
          unit: row.unit || 'oz/ac',
          lotNumber: row.lotNumber || undefined,
          epaRegistrationNumber: row.epaRegistrationNumber || activeWorkOrder.chemicals?.[index]?.epaRegistrationNumber || '',
        })),
      notes: draft.privateNotes,
      comments: draft.customerFacingNotes,
      description: draft.sectionBreakdown,
      followUpNotes: draft.needsAttention
        ? `Needs Attention: ${draft.needsAttention}${draft.whatWentWell ? `\nWhat Went Well: ${draft.whatWentWell}` : ''}`
        : (draft.whatWentWell ? `What Went Well: ${draft.whatWentWell}` : activeWorkOrder.followUpNotes),
      followUpDate: draft.followUpDate || activeWorkOrder.followUpDate,
    };

    setIsSaving(true);
    try {
      // Annual Active Ingredient Cap Guard
      if (next.chemicals && next.chemicals.length > 0) {
        const [locationsList, chemicalsList, settings] = await Promise.all([
          uow.locations.list() as Promise<ServiceLocation[]>,
          uow.chemicals.list() as Promise<Chemical[]>,
          uow.settings.get('default') as Promise<AppSettings | undefined>,
        ]);

        const enforcementMode = settings?.annualAiCapEnforcement || 'warn';
        const capCheck = checkAnnualActiveIngredientCaps({
          workOrder: next,
          locations: locationsList,
          chemicals: chemicalsList,
          enforcementMode,
        });

        if (capCheck.isBlocked) {
          alert(`FEDERAL / STATE LABEL VIOLATION (ANNUAL CAP EXCEEDED):\n\n${capCheck.warnings.join('\n\n')}\n\nCompletion is blocked to prevent illegal active ingredient over-application.`);
          setIsSaving(false);
          return;
        }

        if (capCheck.hasExceeded && capCheck.warnings.length > 0) {
          alert(`ANNUAL ACTIVE INGREDIENT CAP WARNING:\n\n${capCheck.warnings.join('\n\n')}`);
          const existingNotes = next.notes || '';
          const capNote = `[Active Ingredient Cap Warning: ${capCheck.warnings.join(' • ')}]`;
          if (!existingNotes.includes(capNote)) {
            next.notes = existingNotes ? `${existingNotes}\n${capNote}` : capNote;
          }
        }
      }

      if (next.status === 'Completed' || next.status === 'Treated') {
        const compRes = await completeWorkOrderLocal(uow, next, {
          targetStatus: next.status,
        });
        if (!compRes.success) {
          alert(`Failed to complete work order:\n${compRes.error || 'Compliance checks failed.'}`);
          setIsSaving(false);
          return;
        }
        if (onSaveWorkOrder && compRes.updatedWorkOrder) {
          await onSaveWorkOrder(compRes.updatedWorkOrder);
        }
        alert('Work order saved and treatment records finalized.');
      } else {
        await uow.workOrders.put(next);
        if (onSaveWorkOrder) {
          await onSaveWorkOrder(next);
        }
        alert('Work order updated successfully.');
      }
    } catch (err) {
      console.error('Failed to save work order:', err);
      alert('Failed to save work order.');
    } finally {
      setIsSaving(false);
    }
  };

  const isDispatchedOrActive = (status?: string) => {
    return !status || status === "Ready" || status === "In Progress" || status === "Scheduled" || status === "Assigned" || status === "Prepared" || status === "On Hold";
  };

  const isCompletedOrInvoiced = (status?: string) => {
    return status === "Completed" || status === "Invoiced" || status === "Treated";
  };

  const activeCount = useMemo(() => (internalWorkOrders || []).filter((wo) => isDispatchedOrActive(wo.status)).length, [internalWorkOrders]);
  const completedCount = useMemo(() => (internalWorkOrders || []).filter((wo) => isCompletedOrInvoiced(wo.status)).length, [internalWorkOrders]);
  const allCount = (internalWorkOrders || []).length;

  const filteredWorkOrders = useMemo(() => {
    const q = workOrderSearch.trim().toLowerCase();
    return (internalWorkOrders || []).filter((wo) => {
      if (q) {
        return [wo.workOrderNumber, wo.title, wo.customerName, wo.projectName, wo.status]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      }
      if (workOrderFilter === "active") {
        return isDispatchedOrActive(wo.status);
      }
      if (workOrderFilter === "completed") {
        return isCompletedOrInvoiced(wo.status);
      }
      return true;
    });
  }, [internalWorkOrders, workOrderSearch, workOrderFilter]);

  const showSelector = isSelectingWorkOrder || !activeWorkOrder?.id;

  const handleOpsModeChange = async (nextMode: "mix" | "travel" | "fuel-up") => {
    setOpsMode(nextMode);

    if (activeOpsLogId) {
      const type = nextMode === "fuel-up" ? "fueling" : nextMode;
      setIsModeSwitching(true);
      try {
        await operations.transitionLog(activeOpsLogId, type as never, activeWorkOrder?.id);
      } catch {
        // Keep local mode toggle even if transition fails.
      } finally {
        setIsModeSwitching(false);
      }
    }

    if (nextMode === "fuel-up" && activeVehicle) {
      setLogisticsTab("station");
      setLogisticsOpen(true);
    }
  };

  return (
    <>
    {activeVehicle && (
      <LogisticsPortal
        isOpen={logisticsOpen}
        onOpenChange={setLogisticsOpen}
        vehicle={activeVehicle}
        initialTab={logisticsTab}
        activeWorkOrder={activeWorkOrder ?? undefined}
        onRefresh={() => setLogisticsOpen(false)}
      />
    )}
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[900px] lg:max-w-[75vw] p-0 border-l border-white/10 bg-zinc-950 text-zinc-100">
        <SheetHeader className="p-4 border-b border-white/10 bg-zinc-900/80">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <SheetTitle className="text-sm font-black uppercase tracking-wider text-white">Work Orders</SheetTitle>
              <div className="flex items-center gap-2">
                {!showSelector && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsSelectingWorkOrder(true)}
                    className="h-7 text-[9px] font-black uppercase tracking-widest border-white/20 text-zinc-200"
                  >
                    Select Work Order
                  </Button>
                )}
                {(vehicleLabel || assignedTruckLabel) ? (
                  <Badge className="bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 text-[10px] uppercase tracking-widest flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    {vehicleLabel || assignedTruckLabel}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">System Clock</p>
                <p className="text-xs font-black text-white">{currentTimeLabel || format(new Date(), 'HH:mm:ss')}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 sm:col-span-2">
                <p className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">Active Work Order</p>
                <p className="text-xs font-black text-white truncate">
                  {activeWorkOrder ? (
                    <>
                      <span className="text-indigo-400 font-mono font-bold">WO #{activeWorkOrder.workOrderNumber || 'Draft'}</span>
                      <span className="text-zinc-400"> • </span>
                      <span>{activeWorkOrder.title || 'Untitled Work Order'}</span>
                      {activeWorkOrder.projectName && <span className="text-zinc-400 font-normal"> ({activeWorkOrder.projectName})</span>}
                    </>
                  ) : (
                    <span className="text-zinc-500 font-normal italic">No work order selected — pick a dispatched order below</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="h-8 px-3 border-white/20 text-zinc-200">
                <Truck className="h-3.5 w-3.5 mr-1" /> Rig: {assignedTruckLabel || "General Fleet"}
              </Badge>
              {isClockedIn ? (
                <Button size="sm" variant="destructive" onClick={onClockOut} disabled={!onClockOut} className="h-8 text-[10px] font-black uppercase tracking-widest disabled:opacity-40">
                  Clock Out
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={onClockIn} disabled={!onClockIn} className="h-8 text-[10px] font-black uppercase tracking-widest border-blue-400/30 text-blue-300 hover:bg-blue-500/10 disabled:opacity-40">
                  Clock In
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        {showSelector ? (
          <div className="p-4 overflow-y-auto h-[calc(100vh-180px)] space-y-3">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h3 className="text-xs font-black uppercase tracking-widest text-indigo-300">
                  Select Dispatched Work Order
                </h3>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm"
                    variant={workOrderFilter === "active" ? "default" : "outline"}
                    onClick={() => setWorkOrderFilter("active")}
                    className={cn(
                      "h-7 text-[10px] font-bold uppercase tracking-wider",
                      workOrderFilter === "active" ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border-white/10 text-zinc-400"
                    )}
                  >
                    Dispatched / Active ({activeCount})
                  </Button>
                  <Button
                    size="sm"
                    variant={workOrderFilter === "all" ? "default" : "outline"}
                    onClick={() => setWorkOrderFilter("all")}
                    className={cn(
                      "h-7 text-[10px] font-bold uppercase tracking-wider",
                      workOrderFilter === "all" ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border-white/10 text-zinc-400"
                    )}
                  >
                    All ({allCount})
                  </Button>
                  <Button
                    size="sm"
                    variant={workOrderFilter === "completed" ? "default" : "outline"}
                    onClick={() => setWorkOrderFilter("completed")}
                    className={cn(
                      "h-7 text-[10px] font-bold uppercase tracking-wider",
                      workOrderFilter === "completed" ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border-white/10 text-zinc-400"
                    )}
                  >
                    Completed ({completedCount})
                  </Button>
                </div>
              </div>

              <Input
                value={workOrderSearch}
                onChange={(e) => setWorkOrderSearch(e.target.value)}
                placeholder="Search by WO#, title, customer, project, status..."
                className="h-9 bg-zinc-900/70 border-white/10"
              />

              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {filteredWorkOrders.map((wo) => {
                  const statusColor =
                    wo.status === 'In Progress'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                      : wo.status === 'Treated'
                      ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                      : wo.status === 'Invoiced'
                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                      : wo.status === 'Completed'
                      ? 'bg-zinc-800 text-zinc-400 border-white/10'
                      : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30';

                  return (
                    <button
                      key={wo.id}
                      type="button"
                      onClick={() => {
                        if (!wo.id) return;
                        onSelectWorkOrder?.(wo.id);
                        setIsSelectingWorkOrder(false);
                      }}
                      className="w-full rounded-lg border border-white/10 bg-zinc-900/70 p-3 text-left hover:border-indigo-400/50 hover:bg-zinc-800/70 transition-colors space-y-1.5"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-black text-white uppercase tracking-tight truncate">
                          {wo.title || 'Untitled Work Order'}
                        </div>
                        <Badge variant="outline" className={cn("text-[9px] font-bold uppercase tracking-wider shrink-0", statusColor)}>
                          {wo.status || 'Ready'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-zinc-400 font-mono">
                        <span className="text-indigo-300 font-semibold">WO #{wo.workOrderNumber || 'Unassigned'}</span>
                        {wo.customerName && <span>• {wo.customerName}</span>}
                        {wo.projectName && <span className="text-zinc-300">• {wo.projectName}</span>}
                        {((wo as any).targetAcres || (wo as any).totalAcres || wo.billableAcres) && (
                          <span className="text-zinc-500">• {(wo as any).targetAcres || (wo as any).totalAcres || wo.billableAcres} ac</span>
                        )}
                        {wo.roads?.length ? <span className="text-zinc-500">• {wo.roads.length} rds</span> : null}
                      </div>
                    </button>
                  );
                })}
                {filteredWorkOrders.length === 0 && (
                  <div className="text-center py-8 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">
                    No matching work orders found
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
        <>
        <div className="p-4 border-b border-white/10 bg-zinc-900/50">
          <Tabs value={page} onValueChange={(v) => setPage(v as "page1" | "page2" | "page3")}>
            <TabsList className="grid grid-cols-3 h-10 bg-black/40 border border-white/10 rounded-xl">
              <TabsTrigger value="page1" className="text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-indigo-500 data-[state=active]:text-black">Page 1: Scope</TabsTrigger>
              <TabsTrigger value="page2" className="text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-indigo-500 data-[state=active]:text-black">Page 2: Completion</TabsTrigger>
              <TabsTrigger value="page3" className="text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-indigo-500 data-[state=active]:text-black">Page 3: Follow-Up</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-4 p-4 h-[calc(100vh-280px)]">
          <div className="min-w-0 overflow-y-auto pr-1 space-y-4">
            {page === "page1" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <h3 className="text-xs font-black uppercase tracking-widest text-indigo-300 flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5" /> Who It Is For + Scope</h3>
                  <Accordion type="multiple" className="mt-4 w-full">
                  <AccordionItem value="p1-mission" className="border-white/10">
                    <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Mission Context</AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Project</Label><Input value={draft.projectName} onChange={(e) => setField("projectName", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Customer</Label><Input value={draft.customerName} onChange={(e) => setField("customerName", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Requested Work</Label><Input value={draft.workRequested} onChange={(e) => setField("workRequested", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Status</Label>
                          <Select
                            value={draft.status}
                            onValueChange={(value) => {
                              if (value === 'Completed' && activeWorkOrder?.status !== 'Invoiced' && activeWorkOrder?.status !== 'Completed') {
                                alert('Completed requires the work order to be Invoiced first (invoiced and paid).');
                                return;
                              }
                              setField("status", value as WorkOrderPadDraft["status"]);
                            }}
                          >
                            <SelectTrigger className="h-9 bg-zinc-900/70 border-white/10"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Ready">Ready</SelectItem>
                              <SelectItem value="In Progress">In Progress</SelectItem>
                              <SelectItem value="Treated">Treated (Applied in Field)</SelectItem>
                              <SelectItem value="Invoiced">Invoiced</SelectItem>
                              <SelectItem value="Completed" disabled={activeWorkOrder?.status !== 'Invoiced' && activeWorkOrder?.status !== 'Completed'}>Completed (Paid)</SelectItem>
                              <SelectItem value="On Hold">On Hold</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="p1-contact" className="border-white/10">
                    <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Contact And Schedule</AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Date</Label><Input type="date" value={draft.date} onChange={(e) => setField("date", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">App Date</Label><Input type="date" value={draft.appDate} onChange={(e) => setField("appDate", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">County</Label><Input value={draft.county} onChange={(e) => setField("county", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Start Time</Label><Input type="time" value={draft.startTime} onChange={(e) => setField("startTime", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">End Time</Label><Input type="time" value={draft.endTime} onChange={(e) => setField("endTime", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Customer Phone</Label><Input value={draft.customerPhone} onChange={(e) => setField("customerPhone", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Customer Contact</Label><Input value={draft.customerContact} onChange={(e) => setField("customerContact", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Customer Email</Label><Input value={draft.customerEmail} onChange={(e) => setField("customerEmail", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                        <div className="space-y-1.5 md:col-span-3"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Customer Address</Label><Input value={draft.customerAddress} onChange={(e) => setField("customerAddress", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="p1-crew" className="border-white/10">
                    <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Crew And Equipment</AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Assigned Crew (CSV)</Label><Input value={draft.assignedToCsv} onChange={(e) => setField("assignedToCsv", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" placeholder="Lead, Secondary" /></div>
                        <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Target GPA</Label><Input value={draft.gpa} onChange={(e) => setField("gpa", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" /></div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="p1-notes" className="border-white/10">
                    <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Plan Notes</AccordionTrigger>
                    <AccordionContent className="space-y-3">
                      <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Steps / Sections</Label><Textarea value={draft.sectionBreakdown} onChange={(e) => setField("sectionBreakdown", e.target.value)} rows={5} className="bg-zinc-900/70 border-white/10" placeholder="Break work into sections, steps, and field notes for future reference..." /></div>
                      <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Internal Notes (Not Customer-Facing)</Label><Textarea value={draft.privateNotes} onChange={(e) => setField("privateNotes", e.target.value)} rows={4} className="bg-zinc-900/70 border-white/10" placeholder="Private comments, troubleshooting notes, and internal context..." /></div>
                    </AccordionContent>
                  </AccordionItem>
                  </Accordion>
                </div>
              </div>
            )}

            {page === "page2" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <h3 className="text-xs font-black uppercase tracking-widest text-indigo-300 flex items-center gap-2"><CheckSquare className="h-3.5 w-3.5" /> Required Completion Fields</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Preset Type</Label>
                    <Select value={draft.presetType} onValueChange={(value) => setField("presetType", value as WorkOrderPadDraft["presetType"])}>
                      <SelectTrigger className="h-9 bg-zinc-900/70 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="standard-spray">Standard Spray Application</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Road / Area Link</Label>
                    <Input value={draft.roadOrAreaLink} onChange={(e) => setField("roadOrAreaLink", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" placeholder="Road name, route ID, map link, or area reference..." />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Equipment (CSV)</Label>
                    <Input value={draft.equipmentCsv} onChange={(e) => setField("equipmentCsv", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" placeholder="Truck 91, Reel" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Target Weeds (CSV)</Label>
                    <Input value={draft.targetWeedsCsv} onChange={(e) => setField("targetWeedsCsv", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" placeholder="Canada Thistle, Leafy Spurge" />
                  </div>
                  </div>
                  <div className="space-y-1.5 mt-4">
                  <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Completion Data (fields captured + what was applied/how)</Label>
                  <Textarea value={draft.completionFields} onChange={(e) => setField("completionFields", e.target.value)} rows={7} className="bg-zinc-900/70 border-white/10" placeholder="Log rates, chemicals, weather, equipment used, sections completed, and method details..." />
                  </div>
                  <div className="mt-4 rounded-xl border border-white/10 bg-black/20 px-3">
                    <Accordion type="multiple" className="w-full">
                    <AccordionItem value="roads-history" className="border-white/10">
                      <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Previous Road Entries</AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        {draft.roads.map((road, idx) => (
                          <div key={`road-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-6">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Road</Label>
                              <Input value={road.roadName} onChange={(e) => updateRoad(idx, "roadName", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Acres</Label>
                              <Input value={road.acres} onChange={(e) => updateRoad(idx, "acres", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Method</Label>
                              <Input value={road.applicationMethod} onChange={(e) => updateRoad(idx, "applicationMethod", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-1">
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400" onClick={() => setDraft((prev) => ({ ...prev, roads: prev.roads.filter((_, i) => i !== idx) }))}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" className="h-7 text-[9px] uppercase tracking-widest border-white/10" onClick={() => setDraft((prev) => ({ ...prev, roads: [...prev.roads, { roadName: "", acres: "", applicationMethod: "" }] }))}>
                          <Plus className="h-3 w-3 mr-1" /> Add Road
                        </Button>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="manualareas-history" className="border-white/10">
                      <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Previous Manual Area Entries</AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        {draft.manualAreas.map((area, idx) => (
                          <div key={`area-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-6">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Area</Label>
                              <Input value={area.name} onChange={(e) => updateManualArea(idx, "name", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Acres</Label>
                              <Input value={area.acres} onChange={(e) => updateManualArea(idx, "acres", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Method</Label>
                              <Input value={area.applicationMethod} onChange={(e) => updateManualArea(idx, "applicationMethod", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-1">
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400" onClick={() => setDraft((prev) => ({ ...prev, manualAreas: prev.manualAreas.filter((_, i) => i !== idx) }))}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" className="h-7 text-[9px] uppercase tracking-widest border-white/10" onClick={() => setDraft((prev) => ({ ...prev, manualAreas: [...prev.manualAreas, { name: "", acres: "", applicationMethod: "" }] }))}>
                          <Plus className="h-3 w-3 mr-1" /> Add Area
                        </Button>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="chemicals-history" className="border-white/10">
                      <AccordionTrigger className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Previous Chemical Entries</AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        {draft.chemicals.map((chem, idx) => (
                          <div key={`chem-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-3">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Name</Label>
                              <Input value={chem.name} onChange={(e) => updateChemical(idx, "name", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">EPA Reg #</Label>
                              <Input value={chem.epaRegistrationNumber || ''} onChange={(e) => updateChemical(idx, "epaRegistrationNumber", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" placeholder="e.g. 62719-745" />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Rate</Label>
                              <Input value={chem.rate} onChange={(e) => updateChemical(idx, "rate", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-1">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Unit</Label>
                              <Input value={chem.unit} onChange={(e) => updateChemical(idx, "unit", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Lot</Label>
                              <Input value={chem.lotNumber} onChange={(e) => updateChemical(idx, "lotNumber", e.target.value)} className="h-8 bg-zinc-900/70 border-white/10" />
                            </div>
                            <div className="col-span-1">
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400" onClick={() => setDraft((prev) => ({ ...prev, chemicals: prev.chemicals.filter((_, i) => i !== idx) }))}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" className="h-7 text-[9px] uppercase tracking-widest border-white/10" onClick={() => setDraft((prev) => ({ ...prev, chemicals: [...prev.chemicals, { name: "", rate: "", unit: "oz/ac", lotNumber: "", epaRegistrationNumber: "" }] }))}>
                          <Plus className="h-3 w-3 mr-1" /> Add Chemical
                        </Button>
                      </AccordionContent>
                    </AccordionItem>
                    </Accordion>
                  </div>
                </div>
              </div>
            )}

            {page === "page3" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <h3 className="text-xs font-black uppercase tracking-widest text-indigo-300 flex items-center gap-2"><ListTodo className="h-3.5 w-3.5" /> Follow-Up & Customer Add-On Sheet</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Follow-Up Date</Label>
                    <Input type="date" value={draft.followUpDate} onChange={(e) => setField("followUpDate", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Area / Road Focus</Label>
                    <Input value={draft.roadOrAreaLink} onChange={(e) => setField("roadOrAreaLink", e.target.value)} className="h-9 bg-zinc-900/70 border-white/10" placeholder="Route/area reference..." />
                  </div>
                  </div>
                  <div className="space-y-1.5 mt-4">
                  <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">What Went Well</Label>
                  <Textarea value={draft.whatWentWell} onChange={(e) => setField("whatWentWell", e.target.value)} rows={4} className="bg-zinc-900/70 border-white/10" />
                  </div>
                  <div className="space-y-1.5 mt-4">
                  <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Needs Future Attention</Label>
                  <Textarea value={draft.needsAttention} onChange={(e) => setField("needsAttention", e.target.value)} rows={4} className="bg-zinc-900/70 border-white/10" />
                  </div>
                  <div className="space-y-1.5 mt-4">
                  <Label className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Customer-Facing Notes</Label>
                  <Textarea value={draft.customerFacingNotes} onChange={(e) => setField("customerFacingNotes", e.target.value)} rows={4} className="bg-zinc-900/70 border-white/10" placeholder="Thank-you notes, future plan recommendations, and next-step suggestions for the customer..." />
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside className="hidden xl:flex flex-col gap-3 overflow-y-auto pr-1">
            <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/5 p-3">
              <div className="flex items-center gap-2 text-cyan-300 text-[10px] font-black uppercase tracking-widest">
                <CloudSun className="h-3.5 w-3.5" /> Weather Bar
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                  <p className="text-zinc-500 uppercase tracking-wider font-black">Start Temp</p>
                  <p className="text-zinc-100 font-black flex items-center gap-1"><Thermometer className="h-3 w-3" /> {draft.startTemp || "--"}</p>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                  <p className="text-zinc-500 uppercase tracking-wider font-black">Start Wind</p>
                  <p className="text-zinc-100 font-black flex items-center gap-1"><Wind className="h-3 w-3" /> {draft.startWindSpeed || "--"} {draft.startWindDir || ""}</p>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                  <p className="text-zinc-500 uppercase tracking-wider font-black">End Temp</p>
                  <p className="text-zinc-100 font-black flex items-center gap-1"><Thermometer className="h-3 w-3" /> {draft.endTemp || "--"}</p>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                  <p className="text-zinc-500 uppercase tracking-wider font-black">End Wind</p>
                  <p className="text-zinc-100 font-black flex items-center gap-1"><Wind className="h-3 w-3" /> {draft.endWindSpeed || "--"} {draft.endWindDir || ""}</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-center gap-2 text-indigo-300 text-[10px] font-black uppercase tracking-widest">
                <Truck className="h-3.5 w-3.5" /> Rig & Fleet
              </div>
              <div className="mt-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 space-y-1">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Active Rig</p>
                <p className="text-sm font-black text-zinc-100 truncate">{assignedTruckLabel || "General Fleet"}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Status</p>
                <p className="text-xs font-black text-emerald-300">{isClockedIn ? "In Operation" : "Standing By"}</p>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Mode Controls</div>
              <div className="mt-2 grid grid-cols-1 gap-2">
                <Button
                  size="sm"
                  variant={opsMode === "mix" ? "default" : "outline"}
                  onClick={() => void handleOpsModeChange("mix")}
                  disabled={isModeSwitching}
                  className={opsMode === "mix" ? "h-8 text-[10px] font-black uppercase tracking-widest bg-cyan-400 text-black hover:bg-cyan-300" : "h-8 text-[10px] font-black uppercase tracking-widest border-white/20 text-zinc-200"}
                >
                  Mix
                </Button>
                <Button
                  size="sm"
                  variant={opsMode === "travel" ? "default" : "outline"}
                  onClick={() => void handleOpsModeChange("travel")}
                  disabled={isModeSwitching}
                  className={opsMode === "travel" ? "h-8 text-[10px] font-black uppercase tracking-widest bg-amber-400 text-black hover:bg-amber-300" : "h-8 text-[10px] font-black uppercase tracking-widest border-white/20 text-zinc-200"}
                >
                  Travel
                </Button>
                <Button
                  size="sm"
                  variant={opsMode === "fuel-up" ? "default" : "outline"}
                  onClick={() => void handleOpsModeChange("fuel-up")}
                  disabled={isModeSwitching}
                  className={opsMode === "fuel-up" ? "h-8 text-[10px] font-black uppercase tracking-widest bg-rose-400 text-black hover:bg-rose-300" : "h-8 text-[10px] font-black uppercase tracking-widest border-white/20 text-zinc-200"}
                >
                  Fuel Up
                </Button>
              </div>
            </div>
          </aside>
        </div>

        <div className="px-4 py-3 border-t border-white/10 bg-zinc-900/70 text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <div className="flex items-center gap-2"><Route className="h-3.5 w-3.5" /> Autosave Active</div>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-3.5 w-3.5" />
            <span>Internal Draft Persists on Exit</span>
            <Button
              size="sm"
              onClick={handleSaveToWorkOrder}
              disabled={!activeWorkOrder?.id || isSaving}
              className="ml-3 h-7 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40"
            >
              {isSaving ? "Saving..." : "Save to Work Order"}
            </Button>
          </div>
        </div>
        </>
        )}
      </SheetContent>
    </Sheet>
    </>
  );
}
